import React, { useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import ComparisonHeader from '@/components/ComparisonHeader';
import { DetailHeader } from '@/components/responseDetail/DetailHeader';
import { NpsSummary, SentimentSummary, ClassificationSummary } from '@/components/responseDetail/SummaryCards';
import { FeedbackPanel, RatingsPanel, OtherAnswersPanel, legacyFeedbackItems } from '@/components/responseDetail/AnswerSections';
import { CustomerDetailsPanel, ConsentPanel, ClassificationPanel } from '@/components/responseDetail/SidePanels';
import FactorTagsPanel from '@/components/responseDetail/FactorTagsPanel';
import GapLibraryPanel from '@/components/responseDetail/GapLibraryPanel';
import QaMigrate from '@/components/responseDetail/QaMigrate';
import ProfanityRecheck from '@/components/responseDetail/ProfanityRecheck';
import { buildSections, maskedValue, displayTranscripts, profaneVoiceQids } from '@/components/responseDetail/answerModel';

function useView(data, showOriginal) {
  return useMemo(() => {
    const r = data.response;
    const answers = Object.fromEntries(Object.entries(r.answers || {}).map(([k, v]) => [k, maskedValue(r, k, v, showOriginal)]));
    const shown = { ...r, answers };
    const sections = buildSections(shown, data.questions, data.classifications);
    if (shown.nps_score == null && sections.npsQid) {
      const raw = answers[sections.npsQid];
      const n = Number(raw && typeof raw === 'object' ? raw.value : raw);
      if (!Number.isNaN(n)) shown.nps_score = n;
    }
    return { shown, sections, feedback: [...sections.feedback, ...legacyFeedbackItems(shown)],
      transcripts: displayTranscripts(r, showOriginal), profaneAudio: profaneVoiceQids(r) };
  }, [data, showOriginal]);
}

const ProfanityBanner = ({ isAdmin, showOriginal, onToggle }) => (
  <div className="flex items-center justify-between gap-3 text-xs text-[#92400E] bg-[#FFFBEB] border border-[#FCD34D] rounded-xl px-4 py-2.5" data-testid="profanity-detail-banner">
    <span className="flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Profanity detected — {showOriginal ? 'original text shown' : 'masked text shown'}.</span>
    {isAdmin && <button type="button" onClick={onToggle} className="font-semibold underline underline-offset-2" data-testid="profanity-toggle-original">{showOriginal ? 'Show masked' : 'Show original'}</button>}
  </div>
);

export default function DetailBody({ data, isPlatformAdmin, hasGapLibrary, onReload }) {
  const [showOriginal, setShowOriginal] = useState(false);
  const [extra, setExtra] = useState({});
  const { shown, sections, feedback, transcripts, profaneAudio } = useView(data, showOriginal);
  const r = { ...shown, ...extra };
  const rid = r.public_id || r.response_id;
  return (
    <div className="space-y-6">
      <DetailHeader responseRef={rid} branchName={data.branch?.name} submittedAt={r.created_at} surveyName={data.survey?.name} />
      {isPlatformAdmin && <QaMigrate response={r} onMigrated={(p) => setExtra((e) => ({ ...e, ...p }))} />}
      {isPlatformAdmin && <ProfanityRecheck responseId={rid} onDone={onReload} />}
      <ComparisonHeader answers={r.answers} qaEdits={r.qa_edits} surveyConfig={r.survey_config} />
      {r.has_profanity && <ProfanityBanner isAdmin={isPlatformAdmin} showOriginal={showOriginal} onToggle={() => setShowOriginal((v) => !v)} />}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="response-detail-summary">
        <NpsSummary response={r} />
        <SentimentSummary response={r} hasFeedback={feedback.length > 0} />
        <ClassificationSummary items={data.classifications || []} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_400px] gap-6 items-start">
        <div className="space-y-6 min-w-0" data-testid="response-detail-main">
          <FeedbackPanel items={feedback} responseId={r.response_id} derived={transcripts} profaneAudio={profaneAudio} />
          <RatingsPanel items={sections.ratings} legacy={r.ratings} />
          <OtherAnswersPanel items={sections.other} responseId={r.response_id} />
          {!feedback.length && !sections.ratings.length && !sections.other.length && !Object.values(r.ratings || {}).some((v) => Number(v) > 0) && (
            <div className="bg-white border border-dashed border-[#CBD5E1] rounded-2xl p-8 text-center" data-testid="response-detail-no-answers">
              <p className="text-sm font-semibold text-[#334155]">No feedback, ratings or other answers recorded</p>
              <p className="text-xs text-[#64748B] mt-1">This respondent only answered the questions summarised above.</p>
            </div>
          )}
        </div>
        <aside className="space-y-6 min-w-0" data-testid="response-detail-side">
          <FactorTagsPanel responseRef={rid} initial={data} available={data.available_factors || []}
            run={data.factor_tagging_run} canEdit={!!data.permissions?.can_tag_factors} />
          <ClassificationPanel items={sections.classification} />
          <CustomerDetailsPanel items={sections.customer} response={r} />
          <ConsentPanel items={sections.consent} />
          {hasGapLibrary && <GapLibraryPanel responseId={r.response_id} initialIds={r.gap_ids} />}
        </aside>
      </div>
    </div>
  );
}
